import { getSession, signIn } from 'next-auth/react'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/**
 * A failed response, with the status kept on it.
 *
 * `message` is still the backend's `detail`, so every `err.message` in the app
 * reads as before. The status is for the callers that must tell a 403 apart
 * from a network blip: a poller that retries a permission failure every four
 * seconds is asking a question whose answer will not change.
 */
export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/** True when `err` is a response the backend refused on permissions. */
export function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403
}

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signIn('keycloak')
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    throw new ApiError(errorData.detail || 'An API error occurred.', response.status)
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * One named piece of work the server did before it wrote the answer.
 *
 * Added 2026-09-22. The chat stream used to carry exactly one frame shape,
 * `{"text": ...}`. The deterministic answers now also send `{"step": {...}}`
 * frames ahead of the text, so the room can see which panels were read rather
 * than watching a spinner. Every step is sent twice with the same `label`, once
 * `running` and once `done`, which is how a client matches the pair.
 *
 * The model path sends no step frames at all, so a caller that passes no
 * `onStep` behaves exactly as it did before.
 */
export interface StreamStep {
  label: string
  detail?: string
  state: 'running' | 'done'
}

/**
 * One tool the model ran this turn, as the `tools` frame carries it.
 *
 * Deliberately the same shape `/api/ai/chat` returns under `tool_calls`, so the
 * page reads one thing whichever route the answer arrived on.
 */
export interface StreamToolCall {
  id: string
  name: string
  args: Record<string, unknown>
  result?: unknown
}

/**
 * Read a `step` frame's payload, or null if it is not the agreed shape.
 *
 * A malformed step is dropped rather than thrown, unlike a malformed frame: the
 * steps are decoration on an answer, and losing the whole answer because one
 * label arrived as a number would be a far worse trade than losing one tick.
 */
function readStreamStep(raw: unknown): StreamStep | null {
  if (!raw || typeof raw !== 'object') return null
  const step = raw as { label?: unknown; detail?: unknown; state?: unknown }
  if (typeof step.label !== 'string' || !step.label) return null
  if (step.state !== 'running' && step.state !== 'done') return null
  return {
    label: step.label,
    detail: typeof step.detail === 'string' && step.detail ? step.detail : undefined,
    state: step.state,
  }
}

/**
 * POST and read a server-sent event stream, calling `onText` per fragment.
 *
 * Same auth and same base path as every other call; it cannot go through
 * `apiClientFetch` because that one parses the whole body as JSON, which is the
 * thing a stream exists to avoid.
 *
 * `onStep` is optional and additive, added 2026-09-22 for the AI Manager's
 * thinking panel. Existing callers that pass only `onText` are untouched.
 *
 * Returns the assembled text. Throws on anything the caller should fall back
 * from: a bad status, no body, or an error frame from the server.
 */
export async function apiStreamPost(
  endpoint: string,
  data: unknown,
  onText: (fragment: string) => void,
  onStep?: (step: StreamStep) => void,
  onTools?: (tools: StreamToolCall[]) => void
): Promise<string> {
  const session = await getSession()
  const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'text/event-stream' })
  if (session?.accessToken) headers.set('Authorization', `Bearer ${session.accessToken}`)

  const response = await fetch(`${API_URL}${BASE_PATH}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  })

  if (!response.ok || !response.body) {
    throw new Error(`Stream failed: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let full = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    // Frames are separated by a blank line. A partial frame stays in the
    // buffer until the rest of it arrives.
    let split = buffer.indexOf('\n\n')
    while (split !== -1) {
      const frame = buffer.slice(0, split)
      buffer = buffer.slice(split + 2)
      split = buffer.indexOf('\n\n')

      const line = frame.split('\n').find((l) => l.startsWith('data: '))
      if (!line) continue
      const payload = line.slice(6)
      if (payload === '[DONE]') return full

      try {
        const parsed = JSON.parse(payload) as {
          text?: string
          error?: string
          step?: unknown
          tools?: unknown
        }
        if (parsed.error) throw new Error(parsed.error)
        if (parsed.step !== undefined && onStep) {
          const step = readStreamStep(parsed.step)
          if (step) onStep(step)
        }
        // The tools this turn ran. Only the model path sends these, and the page
        // draws the report download buttons and the "Used N tools" chip from
        // them. Without this frame a streamed answer that generated a report
        // lost its download button, which fails silently rather than loudly.
        if (parsed.tools !== undefined && onTools && Array.isArray(parsed.tools)) {
          onTools(parsed.tools as StreamToolCall[])
        }
        if (parsed.text) {
          full += parsed.text
          onText(parsed.text)
        }
      } catch (err) {
        // A malformed frame is a broken stream, not a fragment to skip: the
        // caller has a whole-answer endpoint to fall back to.
        throw err instanceof Error ? err : new Error('Malformed stream frame')
      }
    }
  }

  return full
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },

  /**
   * Upload a file via multipart/form-data.
   * Do NOT set Content-Type — the browser sets it automatically with the boundary.
   */
  uploadFile: <T = unknown>(endpoint: string, file: File, fieldName = 'file'): Promise<T> => {
    const formData = new FormData()
    formData.append(fieldName, file)
    return apiClientFetch<T>(endpoint, {
      method: 'POST',
      body: formData,
      // Do NOT set Content-Type header — browser adds multipart boundary automatically
    })
  },
}

// Default export for backward compatibility
export default apiClientFetch
